import React, { Suspense, useEffect, useRef, useState } from "react";
import { Center } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import SceneErrorBoundary from "./SceneErrorBoundary";
import Suzi, { SUZI_MODEL_URL } from "./Suzi";
import logger from "../utils/vercelRuntimeLogger";
import { RetryHandler } from "../utils/errorHandler";
import { AssetLoadError, normalizeError } from "../utils/appErrors";

const HERO_PREVIEW_CIRCUIT_KEY = "instant3d:hero-preview-disabled";
const HERO_PREVIEW_CIRCUIT_TTL_MS = 60_000;
const HERO_PREVIEW_TIMEOUT_MS = 4000;
const HERO_PREVIEW_MAX_RETRIES = 2;

type RemotePreviewState = "checking" | "ready" | "failed";

function LocalHeroPlaceholder({ loading }: { loading: boolean }) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((_, delta) => {
    if (!meshRef.current) {
      return;
    }

    meshRef.current.rotation.x += delta * 0.2;
    meshRef.current.rotation.y += loading ? delta * 1.4 : delta * 0.6;
  });

  return (
    <Center top>
      <group position={[0, 0.9, 0]}>
        <mesh ref={meshRef} castShadow receiveShadow rotation={[-0.63, 0, 0]}>
          <torusKnotGeometry args={[0.7, 0.18, 128, 24]} />
          <meshStandardMaterial
            color="#6d3434"
            roughness={0.42}
            metalness={0.12}
          />
        </mesh>
        <mesh position={[0, -1.4, 0]} receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[1.15, 64]} />
          <shadowMaterial opacity={0.18} />
        </mesh>
      </group>
    </Center>
  );
}

interface HeroPreviewCircuitState {
  openedAt: number;
  expiresAt: number;
}

function getCircuitState(): HeroPreviewCircuitState | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(HERO_PREVIEW_CIRCUIT_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as HeroPreviewCircuitState;
    if (!parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(HERO_PREVIEW_CIRCUIT_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function shouldOpenCircuit() {
  return getCircuitState() !== null;
}

function clearCircuit() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.removeItem(HERO_PREVIEW_CIRCUIT_KEY);
  } catch {
    // Ignore storage failures; the in-memory fallback still protects the UI.
  }
}

function openCircuit() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const openedAt = Date.now();
    sessionStorage.setItem(
      HERO_PREVIEW_CIRCUIT_KEY,
      JSON.stringify({
        openedAt,
        expiresAt: openedAt + HERO_PREVIEW_CIRCUIT_TTL_MS,
      }),
    );
  } catch {
    // Ignore storage failures; the in-memory fallback still protects the UI.
  }
}

async function verifyRemoteAsset(correlationId: string) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), HERO_PREVIEW_TIMEOUT_MS);

  try {
    const response = await fetch(SUZI_MODEL_URL, {
      method: "HEAD",
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new AssetLoadError("Remote hero preview responded with an invalid status.", {
        assetUrl: SUZI_MODEL_URL,
        stage: "startup-preflight",
        correlationId,
        retryable: response.status >= 500,
        metadata: {
          status: response.status,
          statusText: response.statusText,
        },
      });
    }
  } catch (error) {
    if (error instanceof AssetLoadError) {
      throw error;
    }

    throw new AssetLoadError("Remote hero preview failed the availability check.", {
      assetUrl: SUZI_MODEL_URL,
      stage: "startup-preflight",
      correlationId,
      retryable: true,
      cause: error,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

interface HeroPreviewProps {
  loading: boolean;
}

function HeroPreview({ loading }: HeroPreviewProps) {
  const correlationIdRef = useRef(logger.createCorrelationId("hero"));
  const correlationId = correlationIdRef.current;
  const [remotePreviewState, setRemotePreviewState] = useState<RemotePreviewState>(() =>
    shouldOpenCircuit() ? "failed" : "checking",
  );

  useEffect(() => {
    if (remotePreviewState !== "failed") {
      return;
    }

    const circuitState = getCircuitState();
    if (!circuitState) {
      return;
    }

    const resetPreview = () => {
      clearCircuit();
      setRemotePreviewState("checking");
    };

    const timeoutId = window.setTimeout(
      resetPreview,
      Math.max(circuitState.expiresAt - Date.now(), 0),
    );

    const handleOnline = () => {
      resetPreview();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("online", handleOnline);
    };
  }, [remotePreviewState]);

  useEffect(() => {
    if (remotePreviewState !== "checking") {
      return;
    }

    if (shouldOpenCircuit()) {
      logger.info("HeroPreview", "REMOTE_ASSET_CIRCUIT_OPEN", {
        correlationId,
        assetUrl: SUZI_MODEL_URL,
      });
      return;
    }

    let cancelled = false;

    RetryHandler.withRetry(() => verifyRemoteAsset(correlationId), {
      maxRetries: HERO_PREVIEW_MAX_RETRIES,
      shouldRetry: (error) =>
        !(error instanceof AssetLoadError) || error.retryable,
      onRetry: (attempt, error) => {
        logger.warn("HeroPreview", "REMOTE_ASSET_RETRY", {
          correlationId,
          attempt,
          assetUrl: SUZI_MODEL_URL,
          error: normalizeError(error),
        });
      },
    })
      .then(() => {
        if (cancelled) {
          return;
        }

        setRemotePreviewState("ready");
        logger.info("HeroPreview", "REMOTE_ASSET_READY", {
          correlationId,
          assetUrl: SUZI_MODEL_URL,
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        openCircuit();
        setRemotePreviewState("failed");
        logger.warn("HeroPreview", "REMOTE_ASSET_FALLBACK", {
          correlationId,
          assetUrl: SUZI_MODEL_URL,
          circuitOpen: true,
          circuitResetInMs: HERO_PREVIEW_CIRCUIT_TTL_MS,
          error: normalizeError(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [correlationId, remotePreviewState]);

  if (remotePreviewState !== "ready") {
    return <LocalHeroPlaceholder loading={loading} />;
  }

  return (
    <SceneErrorBoundary
      fallback={<LocalHeroPlaceholder loading={loading} />}
      onError={(error, errorInfo) => {
        openCircuit();
        logger.error(
          "HeroPreview",
          "REMOTE_ASSET_RENDER_FAILED",
          new AssetLoadError("Remote hero preview failed during render.", {
            assetUrl: SUZI_MODEL_URL,
            stage: "runtime-render",
            correlationId,
            retryable: false,
            cause: error,
            metadata: {
              componentStack: errorInfo.componentStack,
            },
          }),
          {
            correlationId,
            assetUrl: SUZI_MODEL_URL,
            componentStack: errorInfo.componentStack,
          },
        );
      }}
    >
      <Suspense fallback={<LocalHeroPlaceholder loading={loading} />}>
        <Center top>
          <Suzi rotation={[-0.63, 0, 0]} scale={2} />
        </Center>
      </Suspense>
    </SceneErrorBoundary>
  );
}

export default HeroPreview;
